import React, { useState, useRef, useCallback } from "react";
import { PluggyConnect } from "react-pluggy-connect";
import { createConnectToken } from "../../service/PluggyServices";
import styles from "./PluggyButton.module.css";
import api from "../../service/api";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

interface PluggyButtonProps {
    cnpj: string;
    conta: string;
    grupo: string;
    updateItemId?: string | null;
    onDisconnectSuccess?: () => void;
}

interface PluggyConnectEvent {
    event: string;
    item?: {
        id: string;
    };
    error?: {
        code: number;
        message: string;
    };
    [key: string]: any;
}

const getToken = () => {
    const connection = localStorage.getItem("connection");
    if (connection) {
        try {
            const connectionObj = JSON.parse(connection);
            return connectionObj?.token;
        } catch (e) {
            return null;
        }
    }
    return null;
};

const getAuthHeaders = () => {
    const token = getToken();
    
    const headers: { [key: string]: string } = {
        'Content-Type': 'application/json'
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return { headers };
};

export default function PluggyButton({ cnpj, conta, grupo, updateItemId, onDisconnectSuccess }: PluggyButtonProps) {
    const [connectToken, setConnectToken] = useState<string | null>(null);
    const [showPluggyConnect, setShowPluggyConnect] = useState(false);
    const hasHandledSuccess = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const isConnected = !!updateItemId;

    const [isExternalFlowActive, setIsExternalFlowActive] = useState(false);

    const handleConnect = async () => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            const token = await createConnectToken();

            if (token) {
                setConnectToken(token);
                setShowPluggyConnect(true);
                hasHandledSuccess.current = false;
                setIsExternalFlowActive(false);
            } else {
                toast.error("Token de conexão Pluggy não retornado.");
                setIsLoading(false);
            }
        } catch (error: any) {
            console.error("Erro ao obter token da Pluggy:", error);
            const status = error.response?.status;
            if (status === 415) {
                toast.error("Erro de Mídia (415): O formato do POST do token está incorreto.");
            } else {
                toast.error("Erro ao conectar com a Pluggy.");
            }
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!updateItemId || isLoading) return;

        setIsLoading(true);
        try {
            const authHeaders = getAuthHeaders();
            const url = `/api/Pluggy/delete-item-id?itemId=${updateItemId}`;
            const response = await api.delete(url, authHeaders);

            if (response.status === 200 || response.status === 204) {
                toast.success("Conexão desconectada com sucesso!");

                if (onDisconnectSuccess) onDisconnectSuccess();

                window.location.reload();

            } else {
                throw new Error("Resposta inesperada do servidor ao tentar desconectar.");
            }

        } catch (error: any) {
            console.error("Erro ao desconectar:", error);
            toast.error("Erro ao desconectar item.");
        }
        setIsLoading(false);
    };

    const handleButtonClick = handleConnect;

    const buttonText = isConnected ? "Desconectar" : "Conectar";
    const loadingText = isConnected ? "Desconectando..." : "Conectando...";

    const sendWebhookData = useCallback(async (idConexao: string) => {
        if (!idConexao) return;

        try {
            const authHeaders = getAuthHeaders();
            const response = await api.post("/api/Pluggy/webhookPending", {
                cnpj,
                conta,
                grupo,
                idConexao,
            }, authHeaders);

            if (response.data.result === "Success") {
                toast.success("Conexão com a Pluggy estabelecida e dados salvos!");
                if (!isConnected && onDisconnectSuccess) onDisconnectSuccess();
            } else {
                toast.error("Dados da conexão enviados, mas o servidor reportou um erro.");
            }
        } catch (error: any) {
            console.error("Erro ao enviar dados da conexão:", error);
            toast.error("Erro fatal ao salvar os dados da conexão no servidor.");
        } finally {
            setIsLoading(false);
            setIsExternalFlowActive(false);
        }
    }, [cnpj, conta, grupo, isConnected, onDisconnectSuccess]);

    const handlePluggyEvent = (event: PluggyConnectEvent) => {
        console.log("Evento do Pluggy:", event);

        if (event.event === "OPEN" || event.event === "OPENED_EXTERNAL_URL") {
            setIsExternalFlowActive(true);
        }

        if (
            event.event === "ITEM_RESPONSE" &&
            event.item?.id &&
            !hasHandledSuccess.current
        ) {
            hasHandledSuccess.current = true;
            sendWebhookData(event.item.id);
        }
    };

    const handlePluggyClose = () => {
        if (!hasHandledSuccess.current) {
            setShowPluggyConnect(false);
            setIsLoading(false);
            setIsExternalFlowActive(false);
        }
    };

    return (
        <>
            <div className={styles.btnGroup}>
                {isConnected ? (
                    <Popconfirm
                        title="Tem certeza que deseja desconectar esta conta?"
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        onConfirm={handleDisconnect}
                        okText="Sim"
                        cancelText="Não"
                    >
                        <button
                            className={styles.btnDisconnect}
                            disabled={isLoading || showPluggyConnect}
                        >
                            {isLoading ? loadingText : buttonText}
                        </button>
                    </Popconfirm>
                ) : (
                    <button
                        onClick={handleButtonClick}
                        className={styles.btnConnect}
                        disabled={isLoading || showPluggyConnect}
                    >
                        {isLoading ? loadingText : buttonText}
                    </button>
                )}
            </div>

            <ToastContainer />

            {showPluggyConnect && connectToken && (
                <PluggyConnect
                    key={connectToken}
                    connectToken={connectToken}
                    includeSandbox={false}
                    language="pt"
                    onEvent={handlePluggyEvent}
                    onClose={handlePluggyClose}
                />
            )}
        </>
    );
}